import { resolveMessageLanguage } from '@shared/core/localized-defaults'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useScrollToAnchorOnceLoaded } from 'src/modules/core/hooks/use-scroll-to-anchor-once-loaded'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { useUpdateProfile } from 'src/modules/user/hooks/use-update-profile'
import {
  MESSAGE_LANGUAGE_ANCHOR_ID,
  type MessageLanguageChoice,
  resolvePreviewBusinessName,
} from './client-message-language-card.domain'

function useSaveMessageLanguage(onSaved: () => void) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const updateProfile = useUpdateProfile()

  const save = async (choice: MessageLanguageChoice) => {
    try {
      await updateProfile.mutateAsync({ message_language: choice })
      onSaved()
      enqueueSnackbar(formatMessage({ id: 'settings.saved' }), { variant: 'success' })
    } catch {
      // The cache still holds the saved value, so the choice stays staged to retry.
      enqueueSnackbar(formatMessage({ id: 'settings.messageLanguage.saveFailed' }), { variant: 'error' })
    }
  }

  return { save, isSaving: updateProfile.isLoading }
}

export function useClientMessageLanguageCard() {
  const { data: profile } = useProfile()
  const [staged, setStaged] = useState<MessageLanguageChoice | undefined>(undefined)
  const { save, isSaving } = useSaveMessageLanguage(() => setStaged(undefined))
  useScrollToAnchorOnceLoaded(MESSAGE_LANGUAGE_ANCHOR_ID, !!profile)

  const saved: MessageLanguageChoice = profile?.message_language ?? null
  const automatic = resolveMessageLanguage({ messageLanguage: null, defaultCountryCode: profile?.default_country_code })
  const isStaged = staged !== undefined && staged !== saved
  const selected = isStaged ? staged : saved

  return {
    profile,
    saved,
    selected,
    isStaged,
    countryCode: profile?.default_country_code ?? null,
    automatic,
    effectiveLanguage: selected ?? automatic.language,
    businessName: resolvePreviewBusinessName(profile?.full_name),
    confirmationsEnabled: profile?.appointment_confirmation === true,
    isSaving,
    stage: setStaged,
    cancel: () => setStaged(undefined),
    save: () => (isStaged ? save(staged) : Promise.resolve()),
  }
}
